import {
  NoSymbolIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import DeleteModal from "@via/components/delete-modal/delete-modal.tsx";
import Table from "@via/components/table/table.tsx";
import { formatDateString } from "@via/utils/helpers.tsx";
import { type ColumnDef } from "@tanstack/react-table";
import { VoucherCodeWithStatsRecord } from "common";
import { type JSX, useCallback, useMemo, useState } from "react";
import dayjs from "dayjs";

type VoucherDeleteItem = {
  id: number;
  name: string;
};

interface VoucherListProps {
  vouchers: VoucherCodeWithStatsRecord[];
  total: number;
  isLoading: boolean;
  isDeleting: boolean;
  isRevoking: boolean;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onEdit: (voucher: VoucherCodeWithStatsRecord) => void;
  onRevoke: (id: number) => void;
  onDelete: (id: number) => void;
}

function getStatus(voucher: VoucherCodeWithStatsRecord): {
  label: string;
  colorClasses: string;
} {
  if (voucher.isRevoked) {
    return { label: "Revoked", colorClasses: "bg-red-100 text-red-800" };
  }
  if (dayjs(voucher.expiresAt).isBefore(dayjs())) {
    return { label: "Expired", colorClasses: "bg-yellow-100 text-yellow-800" };
  }

  if (voucher.redeemedAt) {
    return { label: "Redeemed", colorClasses: "bg-blue-100 text-blue-800" };
  }

  return { label: "Active", colorClasses: "bg-green-100 text-green-800" };
}

export default function VoucherList({
  vouchers,
  total,
  isLoading,
  isDeleting,
  isRevoking,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onRevoke,
  onDelete,
}: VoucherListProps): JSX.Element {
  const [itemToDelete, setItemToDelete] = useState<VoucherDeleteItem | null>(
    null
  );

  const handleDeleteClick = useCallback(
    (voucher: VoucherCodeWithStatsRecord) => {
      setItemToDelete({ id: voucher.id, name: voucher.code });
    },
    []
  );

  const handleConfirmDelete = useCallback(async () => {
    if (itemToDelete) {
      onDelete(itemToDelete.id);
      setItemToDelete(null);
    }
  }, [itemToDelete, onDelete]);

  const columns: ColumnDef<VoucherCodeWithStatsRecord>[] = useMemo(
    () => [
      {
        id: "status",
        header: () => "Status",
        cell: ({ row }) => {
          const status = getStatus(row.original);
          return (
            <span
              className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${status.colorClasses}`}>
              {status.label}
            </span>
          );
        },
        size: 90,
      },
      {
        id: "code",
        header: () => "Code",
        cell: ({ row }) => (
          <span className="font-mono text-sm text-gray-900">
            {row.original.code}
          </span>
        ),
        size: 200,
      },
      {
        id: "redeemedBy",
        header: () => "Redeemed By",
        cell: ({ row }) => {
          if (row.original.redeemedBy === null) {
            return (
              <span className="text-sm italic text-gray-400">Not redeemed</span>
            );
          }
          return (
            <div className="text-xs text-gray-600">
              <div
                className="truncate max-w-[180px]"
                title={row.original.redeemedByEmail ?? undefined}>
                {row.original.redeemedByEmail || "Unknown"}
              </div>
              {row.original.redeemedAt && (
                <div className="text-gray-400">
                  {formatDateString(row.original.redeemedAt, "MMM D, YYYY")}
                </div>
              )}
            </div>
          );
        },
        size: 200,
      },
      {
        id: "expiresAt",
        header: () => "Expires",
        cell: ({ row }) => (
          <span className="text-sm text-gray-500">
            {formatDateString(row.original.expiresAt, "MMM D, YYYY")}
          </span>
        ),
        size: 120,
      },
      {
        id: "createdAt",
        header: () => "Created",
        cell: ({ row }) => (
          <div className="text-xs text-gray-500">
            <div>{formatDateString(row.original.createdAt, "MMM D, YYYY")}</div>
            <div className="text-gray-400">
              by {row.original.createdByEmail || "Unknown"}
            </div>
          </div>
        ),
        size: 150,
      },
      {
        id: "actions",
        header: () => "Actions",
        cell: ({ row }) => {
          const status = getStatus(row.original);
          const canRevoke = status.label === "Active";
          return (
            <div className="flex items-center gap-2">
              {!row.original.isRevoked && (
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    onEdit(row.original);
                  }}
                  className="text-indigo-500 hover:text-indigo-700 p-1 rounded hover:bg-indigo-50"
                  title="Edit"
                  aria-label={`Edit access code: ${row.original.code}`}>
                  <PencilIcon className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
              {canRevoke && (
                <button
                  type="button"
                  disabled={isRevoking}
                  onClick={e => {
                    e.stopPropagation();
                    onRevoke(row.original.id);
                  }}
                  className="text-amber-500 hover:text-amber-700 p-1 rounded hover:bg-amber-50"
                  title="Revoke"
                  aria-label={`Revoke access code: ${row.original.code}`}>
                  <NoSymbolIcon className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  handleDeleteClick(row.original);
                }}
                className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                title="Delete"
                aria-label={`Delete access code: ${row.original.code}`}>
                <TrashIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          );
        },
        size: 120,
      },
    ],
    [onEdit, onRevoke, handleDeleteClick, isRevoking]
  );

  return (
    <>
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Access Codes</h2>
        <p className="mt-1 text-sm text-gray-500">
          View and manage all access codes
        </p>

        <div className="mt-4">
          <Table
            data={vouchers}
            columns={columns}
            isLoading={isLoading}
            page={page}
            setPage={onPageChange}
            pageSize={pageSize}
            setPageSize={onPageSizeChange}
            total={total}
            showPagination={true}
            pageSizeOptions={[10, 20, 40]}
            emptyMessage="No access codes found."
            enableSorting={false}
          />
        </div>
      </div>

      <DeleteModal
        item={itemToDelete}
        setItem={setItemToDelete}
        deleteFunction={handleConfirmDelete}
        isLoading={isDeleting}
        title="Delete Access Code"
        message={name => (
          <>
            Are you sure you want to delete the access code{" "}
            <strong className="font-semibold">&#34;{name}&#34;</strong>? This
            action cannot be undone. All associated redemption records will also
            be deleted.
          </>
        )}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
}
